<template>
  <div v-if="cityCategoriesCardList.length">
    <h2
      v-if="sectionTitle"
      class="text-h2 text-left font-semibold leading-7 mb-4 line-clamp-1"
    >
      {{ sectionTitle }}
    </h2>

    <HorizontalScrollWrapper
      :items-length="cityCategoriesCardList.length"
      class="gap-4 mb-5"
    >
      <NuxtLinkLocale
        v-for="item in cityCategoriesCardList"
        :key="item.title"
        :to="`${item.link}`"
        class="group transition-all hover:scale-[103%]"
      >
        <div class="px-1">
          <TransactionalPageCardListItem v-bind="item" />
        </div>
      </NuxtLinkLocale>
    </HorizontalScrollWrapper>
  </div>
</template>

<script setup lang="ts">
import type { LocalityCategory } from '~/composables/types/locality-category'
import type { Category } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import {
  eventListPageCategoryUrl,
  eventListPageLocalityCategoriesUrl,
} from '~/components/transactionalPage/requestParams'

const { sectionTitle, citySlug, categorySlug, subtitle } = defineProps<{
  sectionTitle?: string
  citySlug?: string
  subtitle?: string
  categorySlug: string
}>()

const cityCategoriesCardList = useState<
  {
    title: string
    subtitle?: string
    imageUrl: string
    link: string
  }[]
>(`transactional-page-city-categories-list-${citySlug || 'category'}`, () => [])

const fullLocalizedUrl = useFullLocalizedUrl()

type TransformedLocalityCategory = {
  permalink: string
  title: string
}

const localityCategoriesPromise = useKftApiFetch<
  KftApiDefaultResponse<LocalityCategory[]>,
  TransformedLocalityCategory[]
>(eventListPageLocalityCategoriesUrl(), {
  transform: (res) => {
    // Transform is useful for eliminating large amounts of unnecessary data
    // that will occupy space inside the document (ssr)
    return (
      res?.data?.map((category) => ({
        title: category.title ?? '',
        permalink: category.permalink ?? '',
      })) || ([] as TransformedLocalityCategory[])
    )
  },
})

const categoriesPromise = useKftApiFetch<KftApiDefaultResponse<Category>>(
  eventListPageCategoryUrl({ categorySlug })
)

const [{ data: categoriesResponse }, { data: localityCategoriesResponse }] =
  await Promise.all([categoriesPromise, localityCategoriesPromise])

const localityCategories = localityCategoriesResponse?.value || []
const categories = computed(() => categoriesResponse?.value?.data || [])

const fnMountCityPageHorizontalList = () => {
  const categoriesObj: Record<string, string> = {}
  if (!localityCategories.length) {
    return
  }

  // For city pages: filter by city and show categories
  if (citySlug) {
    localityCategories
      ?.filter((category) => category.permalink.split('/')[0] === citySlug)
      .forEach((category) => {
        const idx = category.permalink.split('/')[1]
        categoriesObj[idx] = category.title
      })

    cityCategoriesCardList.value = Object.keys(categoriesObj).map((slug) => {
      const catgoryDataObj = Array.isArray(categories.value)
        ? categories.value.find((category: any) => category.slug === slug)
        : null

      return {
        title: categoriesObj[slug],
        subtitle,
        imageUrl: catgoryDataObj
          ? categoryGetters.getThumbnail(catgoryDataObj)
          : '',
        link: fullLocalizedUrl(`/${citySlug}/${slug}`),
      }
    })
  } else {
    // For category pages: filter by category and show cities
    const citiesObj: Record<string, string> = {}

    localityCategories
      ?.filter((category) => category.permalink.split('/')[1] === categorySlug)
      .forEach((category) => {
        const citySlug = category.permalink.split('/')[0]
        citiesObj[citySlug] = category.title
      })

    cityCategoriesCardList.value = Object.keys(citiesObj).map((citySlug) => {
      return {
        title: citiesObj[citySlug],
        subtitle,
        imageUrl: '', // We don't have thumbnails in this context
        link: fullLocalizedUrl(`/${citySlug}/${categorySlug}`),
      }
    })
  }

  cityCategoriesCardList.value = cityCategoriesCardList.value.filter(
    (item) => item.title
  )
}

if (cityCategoriesCardList.value.length === 0) {
  fnMountCityPageHorizontalList()
}
</script>
